import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  FaNetworkWired,
  FaEthernet,
  FaWifi,
  FaShieldAlt,
  FaTimes,
  FaTrash,
  FaSave,
  FaPlus,
  FaPen,
  FaSyncAlt,
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import './NetworkTopology.css';

// ── Configuracion de tipos ─────────────────────────────────────────

const DEVICE_TYPES = {
  router: { label: 'Router', icon: FaNetworkWired, color: '#3b82f6' },
  switch: { label: 'Switch', icon: FaEthernet, color: '#06b6d4' },
  access_point: { label: 'Access Point', icon: FaWifi, color: '#22c55e' },
  firewall: { label: 'Firewall', icon: FaShieldAlt, color: '#ef4444' },
};

const CONNECTION_BASE_STYLES = {
  ethernet: { strokeWidth: 2 },
  fibra: { strokeWidth: 3 },
  wifi: { strokeWidth: 2, strokeDasharray: '6,4' },
};

const DEFAULT_CONN_COLOR = '#64748b';

function getEdgeStyle(tipo, vlan) {
  const base = CONNECTION_BASE_STYLES[tipo] || CONNECTION_BASE_STYLES.ethernet;
  const color = vlan?.color || DEFAULT_CONN_COLOR;
  return { ...base, stroke: color };
}

// ── Nodo custom ────────────────────────────────────────────────────

function DeviceNode({ data, selected }) {
  const config = DEVICE_TYPES[data.tipo] || DEVICE_TYPES.router;
  const Icon = config.icon;

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <Handle type="target" position={Position.Left} />
      <div className={`device-node${selected ? ' selected' : ''}`}>
        <div className={`node-status${data.is_online ? ' online' : ''}`} />
        <div className="node-icon" style={{ background: config.color }}>
          <Icon />
        </div>
        <div className="node-name" title={data.nombre}>
          {data.nombre}
        </div>
        {data.ip && <div className="node-ip">{data.ip}</div>}
      </div>
      <Handle type="source" position={Position.Bottom} />
      <Handle type="source" position={Position.Right} />
    </>
  );
}

const nodeTypes = { device: DeviceNode };

// ── Sidebar catalogo ───────────────────────────────────────────────

function DeviceSidebar({ vlans, onManageVlans }) {
  const onDragStart = (e, tipo) => {
    e.dataTransfer.setData('application/reactflow-tipo', tipo);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="nt-sidebar">
      <h3>Dispositivos</h3>
      {Object.entries(DEVICE_TYPES).map(([tipo, config]) => {
        const Icon = config.icon;
        return (
          <div
            key={tipo}
            className="nt-sidebar-item"
            draggable
            onDragStart={(e) => onDragStart(e, tipo)}
          >
            <div className="icon-wrapper" style={{ background: config.color }}>
              <Icon />
            </div>
            <span>{config.label}</span>
          </div>
        );
      })}

      <h3>VLANs</h3>
      {vlans.length === 0 && (
        <div className="nt-sidebar-hint">Sin VLANs definidas</div>
      )}
      {vlans.map((v) => (
        <div key={v.id} className="nt-sidebar-vlan">
          <div className="vlan-color" style={{ background: v.color }} />
          <span>VLAN {v.numero}</span>
          <span className="vlan-name">{v.nombre}</span>
        </div>
      ))}
      <button className="nt-sidebar-add-btn" onClick={onManageVlans}>
        <FaPlus /> Gestionar VLANs
      </button>
    </div>
  );
}

// ── Modal tipo de conexion (con VLAN) ──────────────────────────────

function ConnectionTypeModal({ vlans, onSelect, onCancel }) {
  const [tipo, setTipo] = useState(null);
  const [vlanId, setVlanId] = useState('');

  const handleConfirm = () => {
    if (!tipo) return;
    onSelect(tipo, vlanId ? parseInt(vlanId) : null);
  };

  return (
    <div className="nt-conn-modal-overlay" onClick={onCancel}>
      <div className="nt-conn-modal" onClick={(e) => e.stopPropagation()}>
        <h4>Tipo de conexion</h4>
        <div className="nt-conn-options">
          {[
            { key: 'ethernet', label: 'Ethernet' },
            { key: 'fibra', label: 'Fibra Optica' },
            { key: 'wifi', label: 'WiFi' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`nt-conn-option${tipo === key ? ' active' : ''}`}
              onClick={() => setTipo(key)}
            >
              <div className={`conn-line ${key}`} />
              <span className="conn-label">{label}</span>
            </button>
          ))}
        </div>

        {vlans.length > 0 && (
          <div className="nt-conn-vlan-select">
            <label>VLAN (opcional)</label>
            <select value={vlanId} onChange={(e) => setVlanId(e.target.value)}>
              <option value="">Sin VLAN</option>
              {vlans.map((v) => (
                <option key={v.id} value={v.id}>
                  VLAN {v.numero} — {v.nombre}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="nt-conn-modal-actions">
          <button
            className="nt-conn-confirm"
            disabled={!tipo}
            onClick={handleConfirm}
          >
            Crear conexion
          </button>
          <button className="nt-conn-cancel" onClick={onCancel}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal gestion de VLANs ─────────────────────────────────────────

function VlanManagerModal({ vlans, onSave, onDelete, onCreate, onClose }) {
  const [form, setForm] = useState({ numero: '', nombre: '', color: '#6366f1', descripcion: '' });
  const [editing, setEditing] = useState(null);

  const resetForm = () => {
    setForm({ numero: '', nombre: '', color: '#6366f1', descripcion: '' });
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!form.numero || !form.nombre) return;
    if (editing) {
      await onSave({ ...form, id: editing });
    } else {
      await onCreate(form);
    }
    resetForm();
  };

  const startEdit = (v) => {
    setEditing(v.id);
    setForm({ numero: v.numero, nombre: v.nombre, color: v.color, descripcion: v.descripcion || '' });
  };

  return (
    <div className="nt-conn-modal-overlay" onClick={onClose}>
      <div className="nt-vlan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nt-detail-header">
          <h3>Gestionar VLANs</h3>
          <button className="close-btn" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="nt-vlan-list">
          {vlans.length === 0 && (
            <div className="nt-vlan-empty">No hay VLANs. Crea la primera.</div>
          )}
          {vlans.map((v) => (
            <div key={v.id} className="nt-vlan-row">
              <div className="vlan-color-dot" style={{ background: v.color }} />
              <span className="vlan-num">VLAN {v.numero}</span>
              <span className="vlan-label">{v.nombre}</span>
              <div className="vlan-actions">
                <button onClick={() => startEdit(v)} title="Editar"><FaPen /></button>
                <button onClick={() => onDelete(v.id)} title="Eliminar"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="nt-vlan-form">
          <h4>{editing ? 'Editar VLAN' : 'Nueva VLAN'}</h4>
          <div className="nt-vlan-form-row">
            <label>
              Numero
              <input
                type="number"
                value={form.numero}
                onChange={(e) => setForm({ ...form, numero: e.target.value })}
                placeholder="10"
              />
            </label>
            <label>
              Nombre
              <input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Administracion"
              />
            </label>
            <label>
              Color
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
              />
            </label>
          </div>
          <div className="nt-vlan-form-actions">
            <button className="btn-save" onClick={handleSubmit}>
              <FaSave /> {editing ? 'Actualizar' : 'Crear'}
            </button>
            {editing && (
              <button className="nt-conn-cancel" onClick={resetForm}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Panel de detalle ───────────────────────────────────────────────

function DeviceDetailPanel({ device, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({ ...device });

  useEffect(() => {
    setForm({ ...device });
  }, [device]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="nt-detail-panel">
      <div className="nt-detail-header">
        <h3>Editar dispositivo</h3>
        <button className="close-btn" onClick={onClose}>
          <FaTimes />
        </button>
      </div>
      <div className="nt-detail-body">
        <label>
          Nombre
          <input
            value={form.nombre || ''}
            onChange={(e) => handleChange('nombre', e.target.value)}
          />
        </label>
        <label>
          Tipo
          <select
            value={form.tipo || 'router'}
            onChange={(e) => handleChange('tipo', e.target.value)}
          >
            {Object.entries(DEVICE_TYPES).map(([key, cfg]) => (
              <option key={key} value={key}>
                {cfg.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Direccion IP
          <input
            value={form.ip || ''}
            onChange={(e) => handleChange('ip', e.target.value)}
            placeholder="192.168.1.1"
          />
        </label>
        <label>
          Direccion MAC
          <input
            value={form.mac || ''}
            onChange={(e) => handleChange('mac', e.target.value)}
            placeholder="AA:BB:CC:DD:EE:FF"
          />
        </label>
        <label>
          Marca
          <input
            value={form.marca || ''}
            onChange={(e) => handleChange('marca', e.target.value)}
            placeholder="Cisco, MikroTik, Ubiquiti..."
          />
        </label>
        <label>
          Modelo
          <input
            value={form.modelo || ''}
            onChange={(e) => handleChange('modelo', e.target.value)}
          />
        </label>
        <label>
          Notas
          <textarea
            value={form.notas || ''}
            onChange={(e) => handleChange('notas', e.target.value)}
            placeholder="Informacion adicional..."
          />
        </label>
      </div>
      <div className="nt-detail-footer">
        <button className="btn-save" onClick={() => onSave(form)}>
          <FaSave /> Guardar
        </button>
        <button className="btn-delete" onClick={() => onDelete(device.id)}>
          <FaTrash />
        </button>
      </div>
    </div>
  );
}

// ── Leyenda de VLANs ───────────────────────────────────────────────

function VlanLegend({ vlans }) {
  if (vlans.length === 0) return null;

  return (
    <Panel position="top-right" className="nt-vlan-legend">
      <div className="nt-vlan-legend-title">VLANs</div>
      {vlans.map((v) => (
        <div key={v.id} className="nt-vlan-legend-item">
          <div className="vlan-color-dot" style={{ background: v.color }} />
          <span>{v.numero} — {v.nombre}</span>
        </div>
      ))}
      <div className="nt-vlan-legend-item">
        <div className="vlan-color-dot" style={{ background: DEFAULT_CONN_COLOR }} />
        <span>Sin VLAN</span>
      </div>
    </Panel>
  );
}

// ── Componente principal ───────────────────────────────────────────

function NetworksView() {
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [vlans, setVlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [pendingConnection, setPendingConnection] = useState(null);
  const [showVlanManager, setShowVlanManager] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [lastPing, setLastPing] = useState(null);

  // Helper para construir un edge de ReactFlow
  const buildEdge = useCallback((c, vlansList) => {
    const vlan = c.vlan || (c.vlan_id ? vlansList.find((v) => v.id === c.vlan_id) : null);
    const style = getEdgeStyle(c.tipo, vlan);
    return {
      id: `e${c.id}`,
      source: String(c.origen_id),
      target: String(c.destino_id),
      type: 'default',
      style,
      label: c.etiqueta || '',
      markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: style.stroke },
      data: { dbId: c.id, tipo: c.tipo, etiqueta: c.etiqueta, vlan_id: c.vlan_id, vlan },
    };
  }, []);

  // Cargar topologia
  const loadTopology = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/api/redes/topologia');

      setVlans(data.vlans || []);

      const flowNodes = data.dispositivos.map((d) => ({
        id: String(d.id),
        type: 'device',
        position: { x: d.pos_x, y: d.pos_y },
        data: { ...d },
      }));

      const flowEdges = data.conexiones.map((c) => buildEdge(c, data.vlans || []));

      setNodes(flowNodes);
      setEdges(flowEdges);
    } catch (err) {
      console.error('Error cargando topologia:', err);
    } finally {
      setLoading(false);
    }
  }, [setNodes, setEdges, buildEdge]);

  useEffect(() => {
    loadTopology();
  }, [loadTopology]);

  // Recolorear edges cuando cambian las VLANs
  const recolorEdges = useCallback((updatedVlans) => {
    setEdges((prev) =>
      prev.map((e) => {
        const vlan = e.data?.vlan_id ? updatedVlans.find((v) => v.id === e.data.vlan_id) : null;
        const style = getEdgeStyle(e.data?.tipo, vlan);
        return { ...e, style, markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: style.stroke }, data: { ...e.data, vlan } };
      })
    );
  }, [setEdges]);

  // Drag & drop desde sidebar
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    async (e) => {
      e.preventDefault();
      const tipo = e.dataTransfer.getData('application/reactflow-tipo');
      if (!tipo || !reactFlowInstance) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });

      const config = DEVICE_TYPES[tipo];
      const nombre = `${config.label} ${nodes.length + 1}`;

      try {
        const { data } = await axiosInstance.post('/api/redes/dispositivos', {
          nombre,
          tipo,
          pos_x: position.x,
          pos_y: position.y,
        });

        const newNode = {
          id: String(data.id),
          type: 'device',
          position: { x: data.pos_x, y: data.pos_y },
          data: { ...data },
        };

        setNodes((prev) => [...prev, newNode]);
      } catch (err) {
        console.error('Error creando dispositivo:', err);
      }
    },
    [reactFlowInstance, nodes.length, setNodes]
  );

  // Guardar posicion al soltar nodo
  const onNodeDragStop = useCallback(async (_event, node) => {
    try {
      await axiosInstance.patch(`/api/redes/dispositivos/${node.id}/posicion`, {
        pos_x: node.position.x,
        pos_y: node.position.y,
      });
    } catch (err) {
      console.error('Error guardando posicion:', err);
    }
  }, []);

  // Seleccionar nodo
  const onNodeClick = useCallback((_event, node) => {
    setSelectedDevice(node.data);
  }, []);

  // Click en canvas vacio = deseleccionar
  const onPaneClick = useCallback(() => {
    setSelectedDevice(null);
  }, []);

  // Conexion entre nodos — mostrar modal de tipo
  const onConnect = useCallback((params) => {
    setPendingConnection(params);
  }, []);

  const handleConnectionType = useCallback(
    async (tipo, vlanId) => {
      if (!pendingConnection) return;

      try {
        const { data } = await axiosInstance.post('/api/redes/conexiones', {
          origen_id: parseInt(pendingConnection.source),
          destino_id: parseInt(pendingConnection.target),
          tipo,
          vlan_id: vlanId,
        });

        const newEdge = buildEdge(data, vlans);
        setEdges((prev) => [...prev, newEdge]);
      } catch (err) {
        console.error('Error creando conexion:', err);
      } finally {
        setPendingConnection(null);
      }
    },
    [pendingConnection, setEdges, vlans, buildEdge]
  );

  // Guardar dispositivo editado
  const handleSaveDevice = useCallback(
    async (form) => {
      try {
        const { data } = await axiosInstance.put(
          `/api/redes/dispositivos/${form.id}`,
          form
        );

        setNodes((prev) =>
          prev.map((n) =>
            n.id === String(data.id) ? { ...n, data: { ...data } } : n
          )
        );
        setSelectedDevice(data);
      } catch (err) {
        console.error('Error guardando dispositivo:', err);
      }
    },
    [setNodes]
  );

  // Eliminar dispositivo
  const handleDeleteDevice = useCallback(
    async (id) => {
      try {
        await axiosInstance.delete(`/api/redes/dispositivos/${id}`);
        setNodes((prev) => prev.filter((n) => n.id !== String(id)));
        setEdges((prev) =>
          prev.filter(
            (e) => e.source !== String(id) && e.target !== String(id)
          )
        );
        setSelectedDevice(null);
      } catch (err) {
        console.error('Error eliminando dispositivo:', err);
      }
    },
    [setNodes, setEdges]
  );

  // Eliminar edge con tecla Delete/Backspace
  const onEdgesDelete = useCallback(async (deletedEdges) => {
    for (const edge of deletedEdges) {
      if (edge.data?.dbId) {
        try {
          await axiosInstance.delete(`/api/redes/conexiones/${edge.data.dbId}`);
        } catch (err) {
          console.error('Error eliminando conexion:', err);
        }
      }
    }
  }, []);

  // ── VLAN CRUD ──────────────────────────────────────────────────

  const handleCreateVlan = useCallback(async (form) => {
    try {
      const { data } = await axiosInstance.post('/api/redes/vlans', {
        numero: parseInt(form.numero),
        nombre: form.nombre,
        color: form.color,
        descripcion: form.descripcion,
      });
      const updated = [...vlans, data].sort((a, b) => a.numero - b.numero);
      setVlans(updated);
    } catch (err) {
      console.error('Error creando VLAN:', err);
    }
  }, [vlans]);

  const handleSaveVlan = useCallback(async (form) => {
    try {
      const { data } = await axiosInstance.put(`/api/redes/vlans/${form.id}`, {
        numero: parseInt(form.numero),
        nombre: form.nombre,
        color: form.color,
        descripcion: form.descripcion,
      });
      const updated = vlans.map((v) => (v.id === data.id ? data : v)).sort((a, b) => a.numero - b.numero);
      setVlans(updated);
      recolorEdges(updated);
    } catch (err) {
      console.error('Error actualizando VLAN:', err);
    }
  }, [vlans, recolorEdges]);

  const handleDeleteVlan = useCallback(async (id) => {
    try {
      await axiosInstance.delete(`/api/redes/vlans/${id}`);
      const updated = vlans.filter((v) => v.id !== id);
      setVlans(updated);
      recolorEdges(updated);
    } catch (err) {
      console.error('Error eliminando VLAN:', err);
    }
  }, [vlans, recolorEdges]);

  // ── Ping all ───────────────────────────────────────────────────

  const handlePingAll = useCallback(async () => {
    setPinging(true);
    try {
      const { data: results } = await axiosInstance.post('/api/redes/ping-all');
      const statusMap = {};
      for (const r of results) {
        statusMap[r.id] = { is_online: r.is_online, last_check: r.last_check };
      }

      setNodes((prev) =>
        prev.map((n) => {
          const status = statusMap[parseInt(n.id)];
          if (!status) return n;
          return {
            ...n,
            data: { ...n.data, is_online: status.is_online, last_check: status.last_check },
          };
        })
      );
      setLastPing(new Date());
    } catch (err) {
      console.error('Error en ping masivo:', err);
    } finally {
      setPinging(false);
    }
  }, [setNodes]);

  // Polling cada 30s
  useEffect(() => {
    if (nodes.length === 0) return;
    // Ping inicial al cargar
    handlePingAll();
    const interval = setInterval(handlePingAll, 30000);
    return () => clearInterval(interval);
  }, [nodes.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="nt-loading">Cargando topologia...</div>;
  }

  return (
    <div className="network-topology">
      <DeviceSidebar
        vlans={vlans}
        onManageVlans={() => setShowVlanManager(true)}
      />

      <div className="nt-canvas" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeDragStop={onNodeDragStop}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onEdgesDelete={onEdgesDelete}
          nodeTypes={nodeTypes}
          fitView
          snapToGrid
          snapGrid={[16, 16]}
          defaultEdgeOptions={{
            type: 'default',
            markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12 },
          }}
        >
          <Controls position="bottom-left" />
          <MiniMap
            position="bottom-right"
            nodeColor={(n) => {
              const tipo = n.data?.tipo;
              return DEVICE_TYPES[tipo]?.color || '#94a3b8';
            }}
            maskColor="rgba(0,0,0,0.08)"
            style={{ border: '1px solid #e2e8f0', borderRadius: 8 }}
          />
          <Background variant="dots" gap={20} size={1} color="#cbd5e1" />
          <VlanLegend vlans={vlans} />
          <Panel position="top-left" className="nt-ping-panel">
            <button
              className={`nt-ping-btn${pinging ? ' pinging' : ''}`}
              onClick={handlePingAll}
              disabled={pinging}
              title="Verificar estado de dispositivos"
            >
              <FaSyncAlt className={pinging ? 'spin' : ''} />
              {pinging ? 'Verificando...' : 'Estado de red'}
            </button>
            {lastPing && (
              <span className="nt-ping-time">
                {lastPing.toLocaleTimeString()}
              </span>
            )}
          </Panel>
          {nodes.length === 0 && (
            <div className="nt-empty-hint">
              <p>Arrastra dispositivos desde el panel izquierdo</p>
              <p>para construir tu topologia de red</p>
            </div>
          )}
        </ReactFlow>
      </div>

      {selectedDevice && (
        <DeviceDetailPanel
          device={selectedDevice}
          onSave={handleSaveDevice}
          onDelete={handleDeleteDevice}
          onClose={() => setSelectedDevice(null)}
        />
      )}

      {pendingConnection && (
        <ConnectionTypeModal
          vlans={vlans}
          onSelect={handleConnectionType}
          onCancel={() => setPendingConnection(null)}
        />
      )}

      {showVlanManager && (
        <VlanManagerModal
          vlans={vlans}
          onCreate={handleCreateVlan}
          onSave={handleSaveVlan}
          onDelete={handleDeleteVlan}
          onClose={() => setShowVlanManager(false)}
        />
      )}
    </div>
  );
}

export { NetworksView as NetworkTopologyView };
export default NetworksView;
